;(function() {
"use strict";

angular.module('core.HttpResource', ['core.assert'])
  .factory('HttpResource', HttpResourceFactory);

HttpResourceFactory.$inject = ['$http', 'assert'];
function HttpResourceFactory($http, assert) {
  /**
   * HTTP interface for RESTful API endpoints. Basic default functionality with exposed overrides.
   * A thin interface surrounding angular's $http service that preconfigures defaults and handles
   * URL formatting.
   */
  class HttpResource {
    /**
     * @param {string} url - API endpoint with optional template (e.g. "/api/examples/:id")
     * @param {string} lookupField - The field to use for single resource requests
     * @param {object} [defaults=ACTION_DEFAULTS]
     * @param {object} [defaults.create] - The defaults for "POST" requests
     * @param {object} [defaults.retrieve] - The defaults for specific resource "GET" requests
     * @param {object} [defaults.list] - The defaults for collection "GET" requests
     * @param {object} [defaults.update] - The defaults for "PUT/PATCH" requests
     * @param {object} [defaults.delete] - The defaults for "DELETE" requests
     */
    constructor(url, lookupField, defaults = ACTION_DEFAULTS) {
      assert(angular.isString(url), '[HttpResource] "url" must be a string');
      assert(angular.isString(lookupField), '[HttpResource] "lookupField" must be a string');
      this.url = url;
      this.lookupField = lookupField;
      this.defaults = defaults;
    }

    /**
     * Creates the appropriate URL for the collection based off of the expected template.
     *
     * @returns {string} The URL of the resource collection
     */
    resourceCollectionUrl() {
      return this.url.replace(`/:${this.lookupField}`, '');
    }

    /**
     * Creates the appropriate URL for a single resource based off of the expected template.
     *
     * @param {number|string} lookup - The identifier of the resource to retrieve
     *
     * @returns {string} The URL for the single resource using the provided "lookup"
     */
    resourceUrl(lookup) {
      return this.url.replace(`:${this.lookupField}`, lookup);
    }

    /**
     * Create a new resource with the provided data
     *
     * @param {object} data - A dictionary of properties for the API to interpret
     * @param {object} [overrides] - See $http "config"
     *
     * @returns {Promise} See {HttpResource#request}
     */
    create(data, overrides) {
      const url = this.resourceCollectionUrl();
      const config = angular.extend({}, this.defaults.create, { data, url }, overrides, { method: 'POST' });
      return this.request(config);
    }

    /**
     * Retrieve a single resource matching the provided id
     *
     * @param {number|string} lookup - The identifier of the resource to retrieve
     * @param {object} params - A dictionary to be encoded as query string parameters
     * @param {object} [overrides] - See $http "config"
     *
     * @returns {Promise} See {HttpResource#request}
     */
    retrieve(lookup, params, overrides) {
      assert(angular.isDefined(lookup), '[HttpResource#retrieve] "lookup" must be defined');
      assert(angular.isObject(params), '[HttpResource#retrieve] "params" must be an object');
      const url = this.resourceUrl(lookup);
      const config = angular.extend({}, this.defaults.retrieve, { params, url }, overrides, { method: 'GET' });
      return this.request(config);
    }

    /**
     * Find resources matching the provided filter criteria
     *
     * @param {object} params - A dictionary to be encoded as query string parameters
     * @param {object} [overrides] - See $http "config"
     *
     * @returns {Promise} See {HttpResource#request}
     */
    list(params, overrides) {
      const url = this.resourceCollectionUrl();
      const config = angular.extend({}, this.defaults.list, { params, url }, overrides, { method: 'GET' });
      return this.request(config);
    }

    /**
     * Update the resource matching the provided ID
     *
     * @param {number|string} lookup - The identifier of the resource to retrieve
     * @param {object} data - dictionary of properties and values to update
     * @param {object} [overrides] - See $http "config"
     *
     * @returns {Promise} See {HttpResource#request}
     */
    update(lookup, data, overrides) {
      assert(angular.isDefined(lookup), '[HttpResource#update] "lookup" must be defined');
      assert(angular.isObject(data), '[HttpResource#update] "data" must be an object');
      const url = this.resourceUrl(lookup);
      const config = angular.extend({}, { method: 'PUT' }, this.defaults.update, { data, url }, overrides);
      assert(APPROVED_UPDATE_METHODS[config.method.toUpperCase()], `[HttpResource#update] "overrides.method" must be ${APPROVED_UPDATE_METHODS_STRING}`);
      return this.request(config);
    }

    /**
     * Delete the resource matching the provided ID
     *
     * @param {number|string} lookup - primary key of the resource
     * @param {object} [overrides] - See $http "config"
     *
     * @returns {Promise} See {HttpResource#request}
     */
    delete(lookup, overrides) {
      assert(angular.isDefined(lookup), '[HttpResource#delete] "lookup" must be defined');
      const url = this.resourceUrl(lookup);
      const config = angular.extend({}, this.defaults.delete, { url }, overrides, { method: 'DELETE' });
      return this.request(config);
    }

    /**
     * Intention is to expose the $http service to consuming services without requiring an
     * additional dependency. Enables the consumer to define custom actions not provided by this
     * interface as well as make related resource requests where applicable.
     *
     * @param {object} config - See $http "config"
     *
     * @returns {Promise} The promise object represents the HTTP Response object defined by the
     *   $http service.
     */
    // eslint-disable-next-line class-methods-use-this
    request(config) {
      assert(angular.isObject(config), '[HttpResource#request] "config" must be an object');
      return $http(config);
    }
  }

  const ACTION_DEFAULTS = {};
  const APPROVED_UPDATE_METHODS = { PATCH: 1, PUT: 1 };
  const APPROVED_UPDATE_METHODS_STRING = Object.keys(APPROVED_UPDATE_METHODS).join(' or ');

  return HttpResource;
}
}());
